import React, { useEffect, useState } from 'react';
import { inviteBrand } from '../shared/vendorInviteBranding';
import { ServiceAreaFields } from './ServiceAreaFields';
import { serviceAreaFromInput } from '../shared/serviceArea';
import { useTheme } from '../ThemeContext';
import {
  VENDOR_APPLICATION_BOX_3_BODY,
  VENDOR_APPLICATION_BOX_3_TITLE,
  applicationTrades,
  vendorApplicationMissing,
  vendorApplicationMissingSentence,
} from '../shared/vendorApplication';
import { VENDOR_SUBMIT_BUTTON } from '../shared/consentCopy';
import { DEFAULT_VENDOR_CATEGORIES } from '../shared/vendorCategories';
import { getApiUrl } from '../utils/platform';

export function VendorApplicationBox3Field({ checked, onChange, disabled }) {
  return (
    <label style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginTop: '1rem', fontSize: '16px', lineHeight: 1.45 }}>
      <input
        type="checkbox"
        checked={checked === true}
        disabled={disabled}
        required
        onChange={(event) => onChange(event.target.checked)}
        style={{ marginTop: '0.2rem', width: '1.1rem', height: '1.1rem', flexShrink: 0 }}
      />
      <span>
        <strong>{VENDOR_APPLICATION_BOX_3_TITLE}</strong>
        {' '}
        {VENDOR_APPLICATION_BOX_3_BODY}
      </span>
    </label>
  );
}

function fieldStyle(colors) {
  return {
    display: 'block',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    margin: '0.35rem 0 0.9rem',
    padding: '0.7rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    fontSize: '16px',
    background: colors.surface,
    color: colors.text,
    colorScheme: 'inherit',
  };
}

function pageStyle(colors, isDark) {
  return {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '640px',
    margin: '0 auto',
    padding: '16px',
    fontFamily: 'sans-serif',
    background: colors.background,
    color: colors.text,
    colorScheme: isDark ? 'dark' : 'light',
  };
}

export function VendorInviteHeader({ agent }) {
  const brand = inviteBrand(agent);
  if (!brand) return null;
  return (
    <header data-testid="vendor-invite-brand" style={{ margin: '0 0 1.25rem' }}>
      <div aria-hidden="true" style={{ height: '4px', borderRadius: '999px', background: brand.brandColor, marginBottom: '1rem' }} />
      {(brand.photoUrl || brand.logoUrl) ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '0.85rem' }}>
          {brand.photoUrl ? (
            <img
              src={brand.photoUrl}
              alt=""
              style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            />
          ) : null}
          {brand.logoUrl ? (
            <img
              src={brand.logoUrl}
              alt=""
              style={{ maxWidth: '148px', maxHeight: '48px', objectFit: 'contain' }}
            />
          ) : null}
        </div>
      ) : null}
      <p style={{ margin: 0, fontSize: '1.15rem', lineHeight: 1.35, fontWeight: 650 }}>
        {brand.headline}
      </p>
    </header>
  );
}

const labelStyle = {
  display: 'block',
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
};

export default function VendorApplicationForm({ token, sponsor = null }) {
  const { colors, isDark } = useTheme();
  const field = fieldStyle(colors);
  const linkColor = isDark ? '#5eead4' : '#0f766e';
  const [agent, setAgent] = useState(sponsor);
  const [inviteState, setInviteState] = useState(token ? 'loading' : 'open');

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('vendor-invite-page');
    return () => root.classList.remove('vendor-invite-page');
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let cancel = false;
    fetch(getApiUrl(`/.netlify/functions/vendor-application?token=${encodeURIComponent(token)}`))
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        return { ok: response.ok, body };
      })
      .then(({ ok, body }) => {
        if (cancel) return;
        if (!ok) {
          setInviteState('inactive');
          return;
        }
        if (body && body.agent) setAgent(body.agent);
        setInviteState('open');
      })
      .catch(() => {
        if (!cancel) setInviteState('open');
      });
    return () => {
      cancel = true;
    };
  }, [token]);
  const [form, setForm] = useState({
    businessName: '',
    trades: [],
    otherTrade: '',
    service_county_fips: [],
    service_statewide: [],
    hours: '',
    stormAvailable: '',
    discount: '',
    discountConditions: '',
    discountEndDate: '',
    submitterName: '',
    licenseNumber: '',
    insuranceNote: '',
    phone: '',
    email: '',
    website: '',
    description: '',
  });
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (!terms) {
      setError('Agree to the Vendor Terms before you submit.');
      return;
    }
    const listed = applicationTrades({
      trades: form.trades,
      otherChecked: form.trades.includes('Other'),
      otherText: form.otherTrade,
    });
    if (listed.error) {
      setError(listed.error);
      return;
    }
    const area = serviceAreaFromInput(form);
    if (area.error) {
      setError(area.error);
      return;
    }
    setSending(true);
    try {
      const response = await fetch(getApiUrl('/.netlify/functions/vendor-application'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          businessName: form.businessName,
          trades: listed.trades,
          serviceCountyFips: area.countyFips,
          serviceStatewide: area.statewide,
          zips: [],
          hours: form.hours,
          stormAvailable: form.stormAvailable === 'yes' ? true : form.stormAvailable === 'no' ? false : null,
          discount: '',
          discountDescription: '',
          discountConditions: '',
          discountEndDate: '',
          submitterName: form.submitterName,
          box1: true,
          box2: true,
          box3: true,
          box4: true,
          box5: false,
          attested: true,
          licenseNumber: form.licenseNumber,
          insuranceNote: form.insuranceNote,
          phone: form.phone,
          email: form.email,
          website: form.website,
          description: form.description,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        const details = result.errors ? Object.values(result.errors).join(' ') : '';
        throw new Error(details || result.error || 'Could not submit');
      }
      setDone(true);
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <main className="vendor-application" style={pageStyle(colors, isDark)}>
        <VendorInviteHeader agent={agent} />
        <h1>Application sent</h1>
        <p>The agent who invited you can accept it with one tap.</p>
      </main>
    );
  }

  if (inviteState === 'inactive') {
    return (
      <main className="vendor-application" style={pageStyle(colors, isDark)}>
        <h1>This invite link is no longer active</h1>
      </main>
    );
  }

  if (inviteState === 'loading') {
    return (
      <main className="vendor-application" style={pageStyle(colors, isDark)}>
        <p>Loading...</p>
      </main>
    );
  }

  const missing = vendorApplicationMissing(form, terms);
  const missingSentence = vendorApplicationMissingSentence(missing);

  return (
    <main className="vendor-application" style={pageStyle(colors, isDark)}>
      <VendorInviteHeader agent={agent} />
      <h1 style={{ color: colors.text }}>List your business</h1>
      <p style={{ color: colors.text, marginTop: 0 }}>This listing is free.</p>
      <form onSubmit={submit} style={{ width: '100%', maxWidth: '100%', minWidth: 0, color: colors.text }}>
        <label style={{ ...labelStyle, color: colors.text }}>Business name<input style={field} value={form.businessName} onChange={set('businessName')} required /></label>
        <label style={{ ...labelStyle, color: colors.text }}>Trade
          <select
            style={field}
            value={form.trades[0] || ''}
            required
            onChange={(event) => {
              const name = event.target.value;
              setForm((current) => ({
                ...current,
                trades: name ? [name] : [],
                otherTrade: name === 'Other' ? current.otherTrade : '',
              }));
            }}
          >
            <option value="">Choose</option>
            {DEFAULT_VENDOR_CATEGORIES.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
            <option value="Other">Other</option>
          </select>
        </label>
        {form.trades[0] === 'Other' ? (
          <label style={{ ...labelStyle, color: colors.text }}>
            Other trade
            <input
              style={field}
              value={form.otherTrade}
              onChange={set('otherTrade')}
              aria-label="Other trade"
              required
            />
          </label>
        ) : null}
        <ServiceAreaFields
          value={form}
          colors={colors}
          onChange={(areaValue) => setForm((current) => ({ ...current, ...areaValue }))}
        />
        <label style={labelStyle}>Email<input style={field} type="email" inputMode="email" autoComplete="email" value={form.email} onChange={set('email')} /></label>
        <label style={labelStyle}>Phone<input style={field} type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={set('phone')} /></label>
        <p style={{ margin: '-0.35rem 0 0.9rem', color: colors.textSecondary || colors.text, fontSize: '16px' }}>Add an email or a phone number.</p>
        <label style={labelStyle}>Hours (optional)<input style={field} value={form.hours} onChange={set('hours')} /></label>
        <label style={labelStyle}>Available after a storm (optional)
          <select style={field} value={form.stormAvailable} onChange={set('stormAvailable')}>
            <option value="">Choose</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </label>
        <label style={labelStyle}>License (optional)<input style={field} value={form.licenseNumber} onChange={set('licenseNumber')} /></label>
        <label style={labelStyle}>Insurance (optional)<input style={field} value={form.insuranceNote} onChange={set('insuranceNote')} /></label>
        <label style={labelStyle}>Website (optional)<input style={field} value={form.website} onChange={set('website')} /></label>
        <label style={labelStyle}>About the business (optional)<textarea style={field} value={form.description} onChange={set('description')} /></label>
        <label style={labelStyle}>Your name (optional)<input style={field} value={form.submitterName} onChange={set('submitterName')} /></label>
        <label style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', margin: '0.4rem 0 1rem', fontSize: '16px', lineHeight: 1.45 }}>
          <input
            type="checkbox"
            checked={terms}
            required
            onChange={(event) => setTerms(event.target.checked)}
            style={{ marginTop: '0.2rem', width: '1.1rem', height: '1.1rem', flexShrink: 0 }}
          />
          <span>
            I agree to the{' '}
            <a href="/terms#vendor-submissions" target="_blank" rel="noopener noreferrer" style={{ color: linkColor, fontWeight: 650 }}>Vendor Terms</a>
          </span>
        </label>
        {error ? <p role="alert" style={{ color: isDark ? '#fecaca' : '#991b1b' }}>{error}</p> : null}
        {missingSentence ? <p role="status" style={{ color: colors.text }}>{missingSentence}</p> : null}
        <button
          type="submit"
          disabled={sending || missing.length > 0}
          onMouseDown={(event) => event.preventDefault()}
          style={{ padding: '0.8rem 1.2rem', background: '#59c4c4', color: '#fff', border: 0, borderRadius: '8px', fontSize: '16px' }}
        >
          {sending ? 'Sending...' : VENDOR_SUBMIT_BUTTON}
        </button>
      </form>
    </main>
  );
}
